import {
	Button,
	Link,
	Progress,
	SpinnerInline,
	Timeout,
	useModal,
} from "@npco/zeller-design-system";
import * as Icon from "@npco/zeller-design-system/icon";
import type { Modify } from "pb.types";
import { useEffect, useState, type ReactNode } from "react";
import { twJoin, twMerge } from "tailwind-merge";

import { Zeller } from "./layout/zeller";

export namespace Layout {
	export type Props = {
		title?: ReactNode;
		logo?: boolean;
		pending?: boolean | "logout" | "submit" | "close";
		onClose?: () => void;
		onLogout?: () => void | Promise<void>;
		/**
		 * If given, will be called on inactivity timeout (default 30s).
		 *
		 * WARNING: If `onTimeout`, `duration`, `visibility`, and/or `label` change
		 * the timeout will reset.
		 */
		timeout?: Modify<
			Omit<Timeout.Props, "onTimeout" | "label"> & {
				/**
				 * If `true`, will show a timeout without a callback.
				 */
				onTimeout: (() => void) | true;
				/**
				 * @default `"10-seconds"`
				 * If `"always"`, will always show the timeout graphic.
				 * If `"10-seconds"`, will show the timeout graphic when 10s remaining.
				 */
				visibility?: "always" | "10-seconds";
				/**
				 * @default `"Closing"`
				 * If includes, "%" will be interpolated as countdown timer, "mm:ss".
				 */
				label?: string;
			},
			{ optional: "duration" }
		>;
		actions?: ReadonlyArray<Action | undefined>;
		actionsStyle?: "justify-end" | "full-width";
		children?: ReactNode;
	} & (
		| { progress: true; progressValue: number; progressLimit: number }
		| { progress?: false; progressValue?: never; progressLimit?: never }
	);

	/**
	 * Layout props a parent route pre-sets for its child pages.
	 *
	 * Not `Partial<Props>`: that distributes over the `progress` union and
	 * yields an arm with `progress?: true` but `progressValue` and
	 * `progressLimit` optional, which satisfies neither real arm — so spreading
	 * it only compiles behind a cast. Progress is per-page anyway, never a
	 * default, so it is dropped before widening.
	 */
	export type Defaults = Partial<
		Omit<Props, "progress" | "progressValue" | "progressLimit">
	>;

	export type Action =
		| {
				label: string;
				onClick: () => void;
				primary?: boolean;
				disabled?: boolean;
		  }
		| undefined;
}

export function Layout({
	title,
	pending = false,
	logo = false,
	onClose,
	onLogout,
	timeout,
	actions,
	actionsStyle = "justify-end",
	children,
	progress,
	progressValue,
	progressLimit,
}: Layout.Props) {
	const modal = useModal({
		variant: "decision",
		title: "Log Out",
		content:
			"Are you sure you want to log out? This will reset this device and require reauthentication.",
		primary: { onClick: onLogout, label: "Log Out" },
		destructive: true,
		overlayBlur: true,
	});

	const timeout_callback = timeout?.onTimeout;
	const timeout_duration_s = timeout?.duration ?? 30; // seconds
	const timeout_visibility = timeout?.visibility ?? "10-seconds";
	const timeout_label = timeout?.label ?? "Closing";

	const [timeoutVisible, setTimeoutVisible] = useState(false);
	const [timeoutCountdown, setTimeoutCountdown] = useState("00:00");

	useEffect(() => {
		if (!timeout_callback) {
			return;
		}

		let $callback_timeout: ReturnType<typeof setTimeout> | undefined =
			undefined;
		let $visible_timeout: ReturnType<typeof setTimeout> | undefined = undefined;
		let $label_interval: ReturnType<typeof setInterval> | undefined = undefined;

		function dispose() {
			$callback_timeout = void clearTimeout($callback_timeout);
			$visible_timeout = void clearTimeout($visible_timeout);
			$label_interval = void clearInterval($label_interval);

			// Cycling to false destroys the graphic and resets when visible again.
			setTimeoutVisible(false);
		}

		const duration_ms = timeout_duration_s * 1_000;
		const expiry_ms = Date.now() + duration_ms;

		if (typeof timeout_callback === "function") {
			$callback_timeout = setTimeout(timeout_callback, duration_ms);
		}

		switch (timeout_visibility) {
			case "always": {
				setTimeoutVisible(true);
				break;
			}
			case "10-seconds": {
				$visible_timeout = setTimeout(
					() => setTimeoutVisible(true),
					Math.max(0, duration_ms - 10_000 /* 10 seconds */),
				);
				break;
			}
		}

		if (timeout_label.includes(COUNTDOWN_TOKEN)) {
			callback();
			$label_interval = setInterval(callback, 500 /* 0.5s */);
			function callback() {
				setTimeoutCountdown(formatRemaining(expiry_ms));
			}
		}

		return dispose;
	}, [timeout_callback, timeout_duration_s, timeout_visibility, timeout_label]);

	return (
		<div className="flex min-h-screen">
			<div className="flex max-h-screen flex-grow">
				<div
					className={twMerge(
						"zds zds-root zds-root-text",
						"flex flex-col h-full flex-grow max-h-screen",
						"overflow-y-auto overflow-x-hidden",
						"[scrollbar-gutter:stable]",
						pending && "overflow-y-hidden",
					)}
				>
					<div
						id="shadow-top"
						className={twMerge(
							"shadow-[0px_0px_5px_1px_rgba(33,35,34,0.2)]",
							"w-full translate-y-[76px] sticky top-0",
							progress && "translate-y-[104px]",
						)}
					/>
					<header className="z-10 flex flex-col px-32 pb-12 pt-32 sticky top-0 bg-inherit">
						<nav className="relative flex h-[32px] flex-row items-center">
							{!!(timeout && timeoutVisible) && (
								<Timeout
									label={timeout_label.replaceAll(
										COUNTDOWN_TOKEN,
										timeoutCountdown,
									)}
									duration={
										timeout_visibility === "10-seconds"
											? 10
											: timeout_duration_s
									}
									className="absolute left-0"
								/>
							)}
							{!!title && (
								<h1 className="flex flex-1 truncate text-left text-2xl/heading">
									{title}
								</h1>
							)}
							{!!logo && <Zeller className="mx-auto h-[24px]" />}
							{pending === "close" && (
								<div
									className={twJoin(
										"absolute right-0",
										"h-[24px] w-[24px]",
										"flex items-center justify-center",
									)}
								>
									<SpinnerInline theme />
								</div>
							)}
							{!!(onClose && pending !== "close") && (
								<button
									type="button"
									aria-label="Close"
									className={twJoin(
										"absolute right-0",
										"h-[24px] w-[24px]",
										"flex items-center justify-center",
										"rounded-8",
										"hover:opacity-60",
										"disabled:opacity-20",
									)}
									onClick={onClose}
									disabled={!!pending}
								>
									<Icon.Crossmark />
								</button>
							)}
						</nav>
						{!!progress && (
							<div className="mt-24 flex">
								<Progress value={progressValue} limit={progressLimit} />
							</div>
						)}
					</header>
					<div
						id="shadow-top-cover"
						className={twMerge(
							"bg-inherit",
							"z-0 h-[6px]",
							"flex-shrink-0 w-full",
						)}
						role="presentation"
					/>
					{children}
					<div
						id="shadow-bottom-cover"
						className={twMerge(
							"bg-inherit",
							"z-10 h-[6px]",
							"flex-shrink-0 flex-grow w-full",
						)}
						role="presentation"
					/>
					<footer className="z-10 min-h-[92px] flex items-center justify-between gap-8 px-32 pb-32 pt-12 sticky bottom-0 bg-inherit">
						{!!onLogout && (
							<div className="leading-none">
								{modal.element}
								<Link
									label="Log Out"
									onClick={() => {
										modal.open();
									}}
									icon={<Icon.Logout />}
									destructive
									disabled={!!pending}
								/>
							</div>
						)}
						<div className="flex flex-1 flex-row-reverse justify-start gap-8">
							{actions?.map((action, index) =>
								action ? (
									<Button
										key={action.label}
										label={action.label}
										onClick={action.onClick}
										ghost={!action.primary}
										disabled={!!pending || action.disabled}
										pending={pending === "submit" && index === 0}
										className={twJoin(
											// All buttons must be at least 220px wide.
											"min-w-[220px]",
											// If "full-width", then all buttons should up the space.
											// But primary buttons will always fill up more space.
											actionsStyle === "full-width" &&
												(action.primary ? "flex-[100]" : "flex-1"),
										)}
									/>
								) : null,
							)}
						</div>
					</footer>
					<div
						id="shadow-bottom"
						className="shadow-[0px_0px_5px_1px_rgba(33,35,34,0.2)] w-full -translate-y-[92px] sticky bottom-0"
					/>
				</div>
			</div>
		</div>
	);
}

const COUNTDOWN_TOKEN = "%";

function formatRemaining(expiry_ms: number): string {
	const delta_s = expiry_ms / 1000 - Date.now() / 1000;
	if (delta_s <= 0) {
		return `00:00`;
	}

	const minutes = `${Math.floor(delta_s / 60)}`.padStart(2, "0");
	const seconds = `${Math.floor(delta_s % 60)}`.padStart(2, "0");
	return `${minutes}:${seconds}`;
}
